<template>
    <div>
        <a-form layout="inline" :rules="rules" ref="filterAmountFormRef" :model="currentValue">
            <template v-if="showRange">
                <a-form-item name="leftRange">
                    <a-input-number
                        autocomplete="off"
                        :precision="precision"
                        class="range-item"
                        :formatter="formatFn"
                        :parser="parserFn"
                        v-model:value="currentValue.leftRange"
                        :min="0"
                        :max="maxCount"
                        :disabled="disabled"
                        :placeholder="placeholderStr"
                        @change="changeValue"
                        @blur="() => whetherReset('leftRange')"
                    />
                </a-form-item>
                <span class="seperator">至</span>
                <a-form-item name="rightRange">
                    <a-input-number
                        autocomplete="off"
                        :precision="precision"
                        class="range-item"
                        :formatter="formatFn"
                        :parser="parserFn"
                        v-model:value="currentValue.rightRange"
                        :min="0"
                        :max="maxCount"
                        :disabled="disabled"
                        :placeholder="placeholderStr"
                        @change="changeValue"
                        @blur="() => whetherReset('rightRange')"
                    />
                </a-form-item>
            </template>

            <template v-else>
                <a-input-number
                    autocomplete="off"
                    :precision="precision"
                    class="custom-comp"
                    :formatter="formatFn"
                    :parser="parserFn"
                    v-model:value="currentValue.specificMount"
                    :min="0"
                    :max="maxCount"
                    :disabled="disabled"
                    :placeholder="placeholderStr"
                    @change="changeValue"
                />
            </template>
        </a-form>
    </div>
</template>

<script lang="ts" setup name="filterAmout">
import { reactive, watch, computed, ref, onMounted } from 'vue';
import { JavaType } from '../config';

const maxCount = 99999999.99;
const isCheckRule = ref(false)
const filterAmountFormRef = ref();
const precision = ref(0)
const currentValue = reactive({
    leftRange: null,
    rightRange: null,
    specificMount: null,
})

const props = defineProps({
    rule: String,
    column: Object,
    value: [Array, String, Number],
    disabled: Boolean,
});

const rules = {
    leftRange: [{
        validator: async (RuleObject, value) => {
            if (!isCheckRule.value) {
                return Promise.resolve();
            }
            if (isEmpty(value)) {
                return Promise.reject(placeholderStr.value);
            }
            if (value < rangeRule.min) {
                return Promise.reject(`值不能小于${rangeRule.min}`);
            }
            if (currentValue.rightRange && value > currentValue.rightRange) {
                return Promise.reject(`最大值不能小于最小值`);
            }
        },
        trigger: ['blur',]
    }],
    rightRange: [ {
        validator: async (RuleObject, value) => {
            if (!isCheckRule.value) {
                return Promise.resolve();
            }
            if (isEmpty(value)) {
                return Promise.reject(placeholderStr.value);
            }
            if (value > rangeRule.max) {
                return Promise.reject(`值不能大于${rangeRule.max}`);
            }
            if (currentValue.leftRange && value < currentValue.leftRange) {
                return Promise.reject(`最小值不能大于最大值`);
            }
        },
        trigger: ['blur']
    }]
}

const showRangeRules = ['eq']
const showRange = computed(() => {
    return showRangeRules.includes(props.rule)
})

function isEmpty(val) {
    return val === '' || val == null;
}

watch(() => props.value, async (value: Array<String>) => {
    if (!props.value) {
        if (!isCheckRule.value) {
            currentValue.leftRange = null;
            currentValue.rightRange = null;
        } else {
            [currentValue.leftRange, currentValue.rightRange] = [rangeRule.min, rangeRule.max];
            changeValue();
        }
        currentValue.specificMount = null;
        return;
    }
    if (showRange.value) {
        [currentValue.leftRange, currentValue.rightRange] = props.value as Array<any>;
    } else {
        currentValue.specificMount = props.value;
    }
}, {
    immediate: true,
})

const placeholderStr = computed(() => {
    return isAmoutComp.value ? '请输入金额' : `请输入${props.column.columnComment}`;
})

const isAmoutComp = computed(() => {
    return props.column?.javaType === JavaType.bigDecimal;
})

function formatFn(value) {
    if (isAmoutComp.value && !isCheckRule.value) {
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    return value;
}

function parserFn(value) {
    if (isAmoutComp.value && !isCheckRule.value) {
        return value.replace(/\$\s?|(,*)/g, '');
    }

    return value;
}

const emit = defineEmits(['update:value', 'change'])

async function changeValue() {
    let resultValue = null;
    if (showRange.value) {
        resultValue = [currentValue.leftRange, currentValue.rightRange];
    } else {
        resultValue = currentValue.specificMount;
    }
    await checkRules()
    emit('update:value', resultValue);
    emit('change', resultValue)
}

async function whetherReset(type) {
    if (!isCheckRule.value) {
        return;
    }
    try {
        await filterAmountFormRef.value.validate(type)
    } catch (error) {
        let newValue;
        if (type === 'leftRange') {
            newValue = rangeRule.min
        } else {
            newValue = rangeRule.max
        }
        currentValue[type] = newValue;
        changeValue();
    }
}

async function checkRules() {
    if (!isCheckRule.value) {
        return;
    }
    filterAmountFormRef.value.validate()
}

const rangeRule = {
    max: 0,
    min: 0,
}
function setRangeConfig() {
    const { javaType, otherCustom } = props.column || {}
    if (javaType !== JavaType.int) {
        precision.value = 2;
    }
    if (otherCustom?.validateRule && Object.keys(otherCustom.validateRule).length) {
        const {max, min} = otherCustom.validateRule;
        isCheckRule.value = true;
        rangeRule.max = max;
        rangeRule.min = min;
        if (!currentValue.leftRange && !currentValue.rightRange) {
            [currentValue.leftRange, currentValue.rightRange] = [min, max];
            changeValue();
        }
    }
    
}

onMounted(() => {
    setRangeConfig()
})

</script>


<style lang="less" scoped>
@seperatorWidth: 40px;
.custom-comp {
    width: 100%;
}
.seperator {
    display: inline-block;
    width: @seperatorWidth;
    text-align: center;
    line-height: 32px;
}

.range-item {
    width: 100%;
}
:deep(.ant-form-item) {
    margin-right: 0!important;
    width: calc((100% - @seperatorWidth) / 2);
}
</style>